import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core'
import {FormBuilder, FormControl} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy'
import {distinctUntilChanged, first, map, skipWhile, switchMap} from 'rxjs/operators';
import {moreOrEqual500, moreOrEqualOne, moreThenZero, required} from '@app/shared/validators'
import {BehaviorSubject, combineLatest, Subscription} from 'rxjs';
import {isEqual, pickBy} from "lodash";
import {AsyncNotMatchValidator} from "@app/shared/validators/async-not-match.validator";
import {
    BtnState,
    getImitationComplexity,
    ICreateObjectByModel,
    ICreateObjectByModelExtra,
    IDriver,
    IEventSet,
    IEventSettings,
    IEventStatusForObject,
    ImitationComplexity,
    ImitationTypes,
    IObject,
    IUpdateObject,
    LtaSelectOptionModel,
    ObjectsService,
    removeNull,
    Types,
    ValueWebsocket,
    WithSaveCancelPanel
} from "@atl/lacerta-ui-common";
import {IEventStatusForm} from "@app/administration/event-sets/components/event-status/event-status.component";
import {EventSetsUtils} from "@app/administration/event-sets/utils/event-sets-utils";
import {RegexValidator} from "@app/shared/validators/regex.validator";
import {FormulaInputComponent} from "@app/modules/form-controls/formula-input/formula-input.component";
import {anyValue} from "@app/shared/validators/anyValue";
import {TranslateService} from "@ngx-translate/core";

const nameValidators = [required, RegexValidator.createValidator(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'lta-errors.regExp')]

@UntilDestroy()
@Component({
    selector: 'lta-object-info-panel',
    templateUrl: 'object-info-panel.component.html',
    styleUrls: ['object-info-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObjectInfoPanelComponent implements OnInit, WithSaveCancelPanel {
    @Output('onCancel') public cancel = new EventEmitter()
    @Output() onFullScreen = new EventEmitter<boolean>()
    @Output() onAddLink = new EventEmitter<IObject>()
    @Output() onRemoveLink = new EventEmitter<IObject>()
    @Input() driversSelectOptions: LtaSelectOptionModel<number>[];
    @Input() drivers: IDriver[];

    eventSetsForObject: Array<IEventSet & { settings: IEventStatusForObject[] }>;
    @Input() set eventSets(value: IEventSet[]) {
        this.eventSetsForObject = value.map(set => {
            const isBool = set?.model_id === 2;
            const settings: IEventStatusForObject[] = set.event_settings?.map(settings => {
                const statusForObject: IEventStatusForObject = {
                    event_class_id: settings.event_class.id,
                    message: settings.msg_template,
                };
                if (isBool) {
                    statusForObject.value_bool = settings.condition.represent === 'true';
                } else {
                    statusForObject.value_int64 = parseInt(settings.condition.represent);
                }
                return statusForObject;
            })
            return {...set, settings}
        });
    }
    public fixBooleanSelectOptions: LtaSelectOptionModel<number>[] = [
        {name: 'True', value: 1},
        {name: 'False', value: 0},
    ]
    @ViewChild(FormulaInputComponent) formulaInput: FormulaInputComponent
    @Input() isLoading: boolean
    public isLoadingValue: boolean = false
    public modelForm = this.formBuilder.control(null, [required])
    @Input() modelsSelectOptions: LtaSelectOptionModel<number>[]
    @Output() public onCreateObject = new EventEmitter<{
        object: ICreateObjectByModel,
        extra: ICreateObjectByModelExtra
    }>()
    @Output() public onUpdateObject = new EventEmitter<{
        object: IUpdateObject,
        id: number,
        linkObject?: IUpdateObject,
        linkId?: number
    }>()
    public readFlagsSelectOptions: LtaSelectOptionModel<boolean>[] = [
        {
            name: this.translateService.get('common.read'),
            value: false
        },
        {
            name: this.translateService.get('common.write'),
            value: true
        },
    ]
    @Input() saveState: BtnState
    @Output() public showFormulaSelect = new EventEmitter<boolean>()
    @Input() websocketValue: ValueWebsocket
    public isEditValue = false
    public isFullScreenCalculator = false
    public valueForm = new FormControl('');
    public skipSwitching = true
    public linkObject: IObject
    public personalEventSettingsStatuses: (IEventStatusForObject & { valid: boolean })[] = [];
    public updateInProgress = false
    private objectFormValueChangesSub: Subscription
    private linkFormValueChangesSub: Subscription
    private siblingsSubject = new BehaviorSubject<IObject[]>([])
    public linkForm = this.formBuilder.group({
        name: new FormControl('', nameValidators, [
            AsyncNotMatchValidator.createValidator(
                this.objectsService.activeObject$.pipe(first(),
                    switchMap(active => this.siblingsSubject.pipe(first(), map(items => items.filter(i => i.name !== active.name)))),
                    map(items => items.map(i => i.name))
                ),
                'lta-errors.notMatch', true
            )
        ]),
        description: new FormControl(''),
        modelId: new FormControl(null, [required]),
    })
    private linkedSiblingsSubject = new BehaviorSubject<IObject[]>([])
    public objectForm = this.formBuilder.group({
        name: new FormControl('', nameValidators, [
            AsyncNotMatchValidator.createValidator(
                this.objectsService.activeObject$.pipe(
                    first(),
                    switchMap(active => {
                        if (active && active.type === Types.Link) {
                            return this.linkedSiblingsSubject.pipe(first(), map(items => {
                                return items?.filter(i => i.name !== active.origin_object.name)
                            }))
                        }
                        return this.siblingsSubject.pipe(first(), map(items => items?.filter(i => i.name !== active.name)))
                    }),
                    map(items => items.map(i => i.name))
                ),
                'lta-errors.notMatch', true
            )
        ]),
        description: new FormControl(''),
        unit: new FormControl(''),
        parent_id: new FormControl(null),
        event_settings: this.formBuilder.group({
            enabled: new FormControl(false),
            event_settings_set_id: new FormControl(null),
        }),
        calculate_settings: this.formBuilder.group({
            enabled: new FormControl(false),
            formula: new FormControl('', [required]),
        }),
        imitation_settings: this.formBuilder.group({
            enabled: new FormControl(false),
            settings: this.formBuilder.group({
                type: new FormControl(0, [required]),
                period_s: new FormControl(0, [moreOrEqualOne]),
                amplitude: new FormControl(0, [moreThenZero]),
                discrete_ms: new FormControl(0, [moreOrEqual500]),
                shift: new FormControl(0),
                start_phase: new FormControl(0),
            })
        }),
        driver_settings: this.formBuilder.group({
            enabled: new FormControl(false),
            settings: this.formBuilder.group({
                driver_object_id: new FormControl(null, [required]),
                write_flag: new FormControl(null, [anyValue]),
                id: new FormControl(null, [required]),
            })
        }),
    })

    constructor(
        private formBuilder: FormBuilder,
        private translateService: TranslateService,
        private objectsService: ObjectsService,
        private cd: ChangeDetectorRef,
    ) {
    }

    public get isLinkObject() {
        return !!this.linkObject
    }

    @Input() set siblings(siblings: IObject[]) {
        this.siblingsSubject.next(siblings)
    }

    @Input() set linkedSiblings(siblings: IObject[]) {
        this.linkedSiblingsSubject.next(siblings)
    }

    @Input()
    public set addObjectToFormula(id: number) {
        if (this.formulaInput && id) {
            this.formulaInput.addObject(id)
        }
    }

    public get activeObject(): IObject | null {
        return this._object
    }

    public get imitationTypes(): LtaSelectOptionModel[] {
        switch (this.activeObject?.type) {
            case Types.Bool:
                return [
                    {name: this.translateService.get('imitation.Bool'), value: ImitationTypes.Bool},
                    {name: this.translateService.get('imitation.Bool fix'), value: ImitationTypes.BoolFix},
                ]
            case Types.Float: {
                return [
                    {name: this.translateService.get('imitation.Fixed'), value: ImitationTypes.FloatFix},
                    {name: this.translateService.get('imitation.Sinusoid'), value: ImitationTypes.FloatSinusoid},
                    {name: this.translateService.get('imitation.Triangle'), value: ImitationTypes.FloatTriangle},
                    {name: this.translateService.get('imitation.Saw'), value: ImitationTypes.FloatSaw},
                    {name: this.translateService.get('imitation.Meandr'), value: ImitationTypes.FloatMeandr},
                    {name: this.translateService.get('imitation.Random'), value: ImitationTypes.FloatRandom},
                ]
            }
            case Types.Int:
                return [
                    {name: this.translateService.get('imitation.Fixed'), value: ImitationTypes.IntFix},
                    {name: this.translateService.get('imitation.Sinusoid'), value: ImitationTypes.IntSinusoid},
                    {name: this.translateService.get('imitation.Triangle'), value: ImitationTypes.IntTriangle},
                    {name: this.translateService.get('imitation.Saw'), value: ImitationTypes.IntSaw},
                    {name: this.translateService.get('imitation.Meandr'), value: ImitationTypes.IntMeandr},
                    {name: this.translateService.get('imitation.Random'), value: ImitationTypes.IntRandom},
                ]
            default:
                return []
        }
    }

    public get modelFormIsValid(): boolean {
        return this.isNewObject ? (!this.modelForm.disabled ? this.modelForm.valid : true) : true
    }

    public get isModelFloat(): boolean {
        return this.modelForm.value === Types.Float
    }

    public get isModelInteger(): boolean {
        return this.modelForm.value === Types.Int
    }

    public get isModelBool(): boolean {
        return this.modelForm.value === Types.Bool
    }

    public get isModelString(): boolean {
        return this.modelForm.value === Types.String
    }

    public get imitationType(): ImitationTypes {
        return this.imitationSettingsType.value
    }

    public get lowImitationComplexity(): boolean {
        return this.imitationType && getImitationComplexity(this.imitationType) === ImitationComplexity.Low
    }

    public get mediumImitationComplexity(): boolean {
        return this.imitationType && getImitationComplexity(this.imitationType) === ImitationComplexity.Medium
    }

    public get highImitationComplexity(): boolean {
        return this.imitationType && getImitationComplexity(this.imitationType) === ImitationComplexity.High
    }

    public get randomImitationComplexity(): boolean {
        return this.imitationType && getImitationComplexity(this.imitationType) === ImitationComplexity.Random
    }

    public get imitationAvailable(): boolean {
        return this.activeObject && (this.activeObject.type === Types.Bool || this.activeObject.type === Types.Float || this.activeObject.type === Types.Int)
    }

    public get hasValue(): boolean {
        return this.activeObject && this.activeObject.type && this.activeObject.type !== Types.Dir && this.activeObject.type !== Types.Link
    }

    public get calculationAvailable(): boolean {
        return this.hasValue
    }

    public get driverAvailable(): boolean {
        return this.hasValue
    }

    public get eventsAvailable(): boolean {
        return this.activeObject && (this.activeObject.type === Types.Bool || this.activeObject.type === Types.Int)
    }

    public get imitationSettingsSwitch() {
        return this.imitationSettings.get('enabled')
    }

    public get calculateSettingsSwitch() {
        return this.calculateSettings.get('enabled')
    }

    public get driverSettingsSwitch() {
        return this.driverSettings.get('enabled')
    }

    public get driverSettingsDriverId() {
        return this.driverSettings.get('settings.id').value
    }

    public get eventSettingsSwitch() {
        return this.eventSettings.get('enabled')
    }

    public get isNewObject() {
        return this.activeObject ? !this.activeObject?.id : false
    }

    private _object: IObject

    @Input()
    public set object(object: IObject | null) {
        this.updateInProgress = false

        if (!object) return
        if (object.type === Types.Link) {
            this.linkObject = object
            object = this.linkObject.origin_object
        } else {
            this.linkObject = null
        }
        if (this.linkFormValueChangesSub) this.linkFormValueChangesSub.unsubscribe()

        if (this.linkObject) {
            this.linkForm.reset({
                name: this.linkObject.name,
                description: this.linkObject.description,
                modelId: {value: this.linkObject.model.id, disabled: true},
            })
            this.linkForm.markAsPristine()
            this.linkFormValueChangesSub = this.initLinkFormValueChanges()
        }

        this.isFullScreenCalculator = false
        this.isEditValue = false
        this.isLoadingValue = false
        this.skipSwitching = true
        if (this.objectFormValueChangesSub) this.objectFormValueChangesSub.unsubscribe()
        this.objectForm.reset()
        this.modelForm.reset()
        this._object = object
        this.showFormulaSelect.emit(false)

        if (object) {
            this.objectForm.patchValue(object)
            this.modelForm.patchValue(object.model?.id)
            this.objectFormValueChangesSub = this.initObjectFormValueChanges()
        }

        this.personalEventSettingsStatuses = [];
        if (object?.event_settings) {
            const switchOn = !object?.event_settings.personal;
            this.eventSettings.get('enabled').patchValue(switchOn);

            if (!switchOn) {
                this.personalEventSettingsStatuses = (object.event_settings.event_settings ?? [])
                    .map(s => ({...s, valid: true}))
            }
        }

        this.objectForm.markAsPristine()
        if (this.isNewObject) {
            this.modelForm.enable()
            this.imitationSettingsForm.disable()
            this.calculateFormulaControl.disable()
            this.driverSettingsForm.disable()
            this.eventSettings.disable()
        } else {
            this.modelForm.disable()
            this.eventSettings.enable()
        }
        setTimeout(() => {
            this.skipSwitching = false
        }, 300);
    }

    public get driverWriteFlag() {
        return this.driverSettingsForm.get('write_flag')
    }

    public get eventSetsSelectOptions(): LtaSelectOptionModel<number>[] {
        return this.eventSetsForObject
            .filter(item => item.model_id === this._object.model.id)
            .map(item => ({name: item.name, value: item.id}));
    }

    public get eventSettingsStatuses(): IEventStatusForObject[] {
        let statuses: IEventStatusForObject[];

        if (this.eventSettingsSwitch.value) {
            const id = this.eventSettings.value.event_settings_set_id;
            statuses = this.eventSetsForObject.find(item => item.id === id)?.settings ?? [];
        } else {
            statuses = this.personalEventSettingsStatuses;
        }

        return statuses;
    }

    public get showAddSettingsStatusBtn(): boolean {
        const isBoolModel = this.activeObject.model.id === 2;
        const canAddByLimit = isBoolModel ? this.eventSettingsStatuses.length < 2 : true;
        const isPersonal = !this.eventSettingsSwitch.value;
        return isPersonal && canAddByLimit;
    }

    public get saveDisabled(): boolean {
        const isStateReady = this.saveState === 'SAVE' || this.saveState === 'CREATE';
        const wasLoad = !this.isLoading;
        const isValid = wasLoad && this.isValid && this.isDirty && isStateReady;
        return !isValid;
    }

    public get isValid() {
        const objectFormIsValid = (((this.linkObject && this.linkObject.origin_object) || !this.linkObject) ? this.objectForm.valid : true)
        const linkFormIsValid = this.linkObject ? this.linkForm.valid : true
        return objectFormIsValid && this.modelFormIsValid && linkFormIsValid && this.isValidEventSettingsStatuses;
    }

    public get isDirty() {
        return this.objectForm.dirty || (this.linkObject ? this.linkForm.dirty : false) || this.isEventSettingsHaveChanges;
    }

    private get imitationSettings() {
        return this.objectForm.get('imitation_settings')
    }

    private get driverSettings() {
        return this.objectForm.get('driver_settings')
    }

    private get imitationSettingsForm() {
        return this.imitationSettings.get('settings')
    }

    private get driverSettingsForm() {
        return this.driverSettings.get('settings')
    }

    private get driverSettingsFormDriver() {
        return this.driverSettingsForm.get('id')
    }

    private get driverSettingsFormObject() {
        return this.driverSettingsForm.get('driver_object_id')
    }

    private get imitationSettingsType() {
        return this.imitationSettingsForm.get('type')
    }

    private get eventSettings() {
        return this.objectForm.get('event_settings')
    }

    private get calculateSettings() {
        return this.objectForm.get('calculate_settings')
    }

    private get calculateFormulaControl() {
        return this.calculateSettings.get('formula')
    }

    private get isValidEventSettingsStatuses(): boolean {
        let isValid;
        const personal = !this.eventSettingsSwitch.value;

        if (personal) {
            const statuses = this.personalEventSettingsStatuses;
            const values = statuses.map(s => s.hasOwnProperty('value_bool') ? s.value_bool : s.value_int64);
            const valuesSet = new Set(values);
            isValid = statuses.every(s => s.valid) && values.length === valuesSet.size;
        } else {
            isValid = this.eventSettings.value.event_settings_set_id !== null;
        }

        return isValid;
    }

    private get isEventSettingsHaveChanges() {
        if (!this.activeObject) return false;
        // 2 - bool; 3 - int
        const modelId = this.activeObject.model.id;
        const hasSettings = [2, 3].includes(modelId);

        if (!hasSettings) return false;

        const personal = !this.eventSettingsSwitch.value;
        const haveChanges = personal ? this.isPersonalEventSettingsHaveChanges : this.isEventSetsHaveChanges;
        const switchChanged = (this.activeObject?.event_settings?.personal ?? true) != personal;
        return haveChanges || switchChanged;
    }

    private get isEventSetsHaveChanges() {
        const initial = this.activeObject?.event_settings?.event_settings_set_id;
        const current = this.eventSettings.value.event_settings_set_id;
        return !isEqual(current, initial);
    }

    private get isPersonalEventSettingsHaveChanges() {
        const initial = this.activeObject?.event_settings?.event_settings ?? [];
        const current = structuredClone(this.personalEventSettingsStatuses).map(s => {
            delete s.valid;
            return s;
        })
        return !isEqual(current, initial);
    }

    public ngOnInit(): void {
        this.setValidators()
    }

    public onSave() {
        if (this.formulaInput?.value) this.formulaInput.addObjectElement(this.formulaInput.value).subscribe()
        if (this.isNewObject) {
            const object: ICreateObjectByModel = {
                name: this.objectForm.get('name').value,
                description: this.objectForm.get('description').value,
                unit: this.objectForm.get('unit').value,
            }
            const extra: ICreateObjectByModelExtra = {
                model_Id: +this.modelForm.value,
                parent_id: +this.objectForm.get('parent_id').value,
            }
            this.onCreateObject.emit({
                object,
                extra
            })
        } else {
            const newObject = this.objectForm.getRawValue()
            if (newObject.imitation_settings.settings) {
                newObject.imitation_settings.settings.amplitude = newObject.imitation_settings.settings.amplitude && Number(newObject.imitation_settings.settings.amplitude)
                newObject.imitation_settings.settings.discrete_ms = newObject.imitation_settings.settings.discrete_ms && Number(newObject.imitation_settings.settings.discrete_ms)
                newObject.imitation_settings.settings.period_s = newObject.imitation_settings.settings.period_s && Number(newObject.imitation_settings.settings.period_s)
                newObject.imitation_settings.settings.shift = newObject.imitation_settings.settings.shift && Number(newObject.imitation_settings.settings.shift)
                newObject.imitation_settings.settings.start_phase = newObject.imitation_settings.settings.start_phase && Number(newObject.imitation_settings.settings.start_phase)
            }
            let object: IUpdateObject = {}
            let newEventSettings: IEventSettings;

            if (this.activeObject) {
                if (this.activeObject.name !== newObject.name) {
                    object.name = newObject.name
                }

                if (this.activeObject.description !== newObject.description) {
                    object.description = newObject.description
                }

                if (this.activeObject.unit !== newObject.unit) {
                    object.unit = newObject.unit
                }

                if (this.activeObject.parent_id !== newObject.parent_id) {
                    object.parent_id = newObject.parent_id
                }

                if (this.isEventSettingsHaveChanges) {
                    // @ts-ignore
                    newEventSettings = {};
                    if (newObject.event_settings.enabled) {
                        newEventSettings['existing_event_settings_set_id'] = newObject.event_settings.event_settings_set_id;
                    } else {
                        if (this.personalEventSettingsStatuses.length > 0) {
                            const settings = structuredClone(this.personalEventSettingsStatuses).map(s => {
                                delete s.valid;
                                return s;
                            });
                            newEventSettings['new_event_settings_set'] = {
                                model_id: this.activeObject.model.id,
                                event_settings: settings
                            };
                        }
                    }
                }

                if (!isEqual(this.activeObject.driver_settings, newObject.driver_settings)) {
                    // @ts-ignore
                    object.driver_settings = {}
                    if (!!this.activeObject.driver_settings?.enabled !== !!newObject.driver_settings.enabled) {
                        object.driver_settings.enabled = newObject.driver_settings.enabled
                    }
                    if (!isEqual(this.activeObject.driver_settings?.settings, newObject.driver_settings?.settings)) {
                        // @ts-ignore
                        object.driver_settings.settings = {}
                        if (!isEqual(this.activeObject.driver_settings?.settings.driver_object_id, newObject.driver_settings?.settings.driver_object_id)) {
                            object.driver_settings.settings.driver_object_id = newObject.driver_settings?.settings.driver_object_id
                        }
                        if (!isEqual(this.activeObject.driver_settings?.settings.id, newObject.driver_settings?.settings.id)) {
                            object.driver_settings.settings.id = newObject.driver_settings?.settings.id
                        }
                        // if (!isEqual(this.activeObject.driver_settings?.settings.name, newObject.driver_settings?.settings.name)) {
                        //     object.driver_settings.settings.name = newObject.driver_settings?.settings.name
                        // }
                        if (!isEqual(this.activeObject.driver_settings?.settings.write_flag, newObject.driver_settings?.settings.write_flag)) {
                            object.driver_settings.settings.write_flag = newObject.driver_settings?.settings.write_flag
                        }
                        if (isEqual(object.driver_settings?.settings, {})) {
                            object.driver_settings.settings = undefined
                        }
                    }
                    if (isEqual(object.driver_settings, {})) {
                        object.driver_settings = undefined
                    }
                    if (this.activeObject.driver_settings?.enabled === undefined && newObject.driver_settings.enabled === false) {
                        object.driver_settings = undefined
                    }
                }

                if (!isEqual(this.activeObject.imitation_settings, newObject.imitation_settings)) {
                    // @ts-ignore
                    object.imitation_settings = {}

                    if (!!this.activeObject.imitation_settings?.enabled !== !!newObject.imitation_settings.enabled) {
                        object.imitation_settings.enabled = newObject.imitation_settings.enabled
                    }
                    if (!isEqual(this.activeObject.imitation_settings?.settings, newObject.imitation_settings?.settings) && newObject.imitation_settings.enabled) {
                        object.imitation_settings.settings = newObject.imitation_settings.settings
                    }
                    if (isEqual(object.imitation_settings, {})) {
                        object.imitation_settings = undefined
                    }
                    if (this.activeObject.imitation_settings?.enabled === undefined && newObject.imitation_settings.enabled === false) {
                        object.imitation_settings = undefined
                    }
                }

                if (!isEqual(this.activeObject.calculate_settings, newObject.calculate_settings)) {
                    // @ts-ignore
                    object.calculate_settings = {}
                    if (!!this.activeObject.calculate_settings?.enabled !== !!newObject.calculate_settings.enabled) {
                        object.calculate_settings.enabled = newObject.calculate_settings.enabled
                    }
                    if (this.activeObject.calculate_settings?.formula !== newObject.calculate_settings?.formula && newObject.calculate_settings?.formula !== '') {
                        object.calculate_settings.formula = newObject.calculate_settings.formula
                    }
                    if (isEqual(object.calculate_settings, {})) {
                        object.calculate_settings = undefined
                    }
                }
            }
            object = removeNull(object)

            if (newEventSettings) {
                object.event_settings = newEventSettings;
            }

            if (isEqual(object, {})) {
                object = null
            }

            let linkObject: IUpdateObject = {}

            if (this.linkObject && this.linkForm.dirty) {
                if (!isEqual(this.linkObject.name, this.linkForm.value.name)) {
                    linkObject.name = this.linkForm.value.name
                }

                if (!isEqual(this.linkObject.description, this.linkForm.value.description)) {
                    linkObject.description = this.linkForm.value.description
                }
            }

            linkObject = removeNull(linkObject)

            if (isEqual(linkObject, {})) {
                linkObject = null
            }

            if (!object && !linkObject) {
                this.objectForm.markAsPristine()
                return
            }

            this.onUpdateObject.emit({object, id: this._object?.id, linkObject, linkId: this.linkObject?.id})
        }
    }

    public onUnselectDriverElement() {
        this.updateInProgress = true
        this.onUpdateObject.emit({
            // @ts-ignore
            object: {driver_settings: {}},
            id: this._object?.id
        })
    }

    public changeValue() {
        this.isEditValue = true
        this.valueForm.markAsPristine()
        if (this.isModelFloat || this.isModelString || this.isModelInteger) {
            this.valueForm.patchValue(String(this.websocketValue?.value))
        } else if (this.isModelBool) {
            this.valueForm.patchValue(this.websocketValue?.value ? '1' : '0')
        }
    }

    public saveValue() {
        if (this.valueForm.value.toString()) {
            this.isLoadingValue = true

            const value = this.isModelFloat || this.isModelString || this.isModelInteger ?
                this.valueForm.value : Boolean(this.valueForm.value)
            this.objectsService.setValue(this._object.id, value as string).subscribe(() => {
                this.isEditValue = false
                this.isLoadingValue = false
                this.cd.markForCheck()
            }, error => {
                this.isLoadingValue = false
                this.cd.markForCheck()
            })
        } else {
            this.isEditValue = false
        }
    }

    public onCancel() {
        this.isFullScreenCalculator = false
        this.cancel.emit()
    }

    public toggleFullScreenCalculator(isFullScreen: boolean) {
        this.isFullScreenCalculator = isFullScreen
        if (!isFullScreen) {
            this.isLoading = true
            setTimeout(() => {
                this.isLoading = false
            });
        }
        this.onFullScreen.emit(isFullScreen)
    }

    public addPersonalEventSettingsStatus() {
        this.personalEventSettingsStatuses.push({
            event_class_id: null,
            message: null,
            valid: false,
        })
        this.saveState = this.activeObject?.id ? 'SAVE' : 'CREATE'
    }

    public updatePersonalEventSettingsStatus(index: number, form: IEventStatusForm) {
        const {value, eventClass, eventText, isValid} = form;
        const status = this.personalEventSettingsStatuses[index];
        status.event_class_id = eventClass;
        status.message = eventText;
        status.valid = isValid;

        if (typeof value == 'boolean') {
            delete status.value_int64;
            status.value_bool = value;
        } else {
            status.value_int64 = +value;
            delete status.value_bool;
        }

        this.saveState = this.activeObject?.id ? 'SAVE' : 'CREATE'
    }

    public deletePersonalEventSettingsStatus(i: number) {
        this.personalEventSettingsStatuses.splice(i, 1);
        this.saveState = this.activeObject?.id ? 'SAVE' : 'CREATE'
    }

    public checkIsUniqueStatus(index: number): boolean {
        const arr = EventSetsUtils.validateStatusesForObject(this.personalEventSettingsStatuses);
        return arr[index] == 'UNIQUE';
    }

    private initObjectFormValueChanges() {
        return this.objectForm.valueChanges
            .pipe(
                untilDestroyed(this),
                skipWhile(() => this.saveState === 'SAVING' || this.saveState === 'CREATING'),
                distinctUntilChanged((x, y) => {
                    return isEqual(x, y)
                }))
            .subscribe((f) => {
                this.saveState = this.activeObject?.id ? 'SAVE' : 'CREATE'
            })
    }

    private initLinkFormValueChanges() {
        return this.linkForm.valueChanges
            .pipe(
                untilDestroyed(this),
                skipWhile(() => this.saveState === 'SAVING' || this.saveState === 'CREATING'),
                distinctUntilChanged((x, y) => {
                    return isEqual(x, y)
                }))
            .subscribe((f) => {
                this.saveState = this.linkObject?.id ? 'SAVE' : 'CREATE'
            })
    }

    private setValidators() {
        let changeIndex: number
        combineLatest([this.driverSettingsSwitch.valueChanges, this.imitationSettingsSwitch.valueChanges, this.calculateSettingsSwitch.valueChanges])
            .pipe(
                untilDestroyed(this),
                distinctUntilChanged((x, y) => {
                    const change = pickBy(y, (v, k) => !isEqual(x[k], v)) as Object
                    changeIndex = Number(Object.keys(change)[0])
                    return isEqual(x, y)
                })
            )
            .subscribe(([driverSwitch, imitationSwitch, calculateSwitch]) => {
                if (this.skipSwitching) return;
                if (changeIndex === 0 && driverSwitch) {
                    this.imitationSettingsSwitch.patchValue(false, {emitEvent: false})
                    this.calculateSettingsSwitch.patchValue(false, {emitEvent: false})
                } else if (driverSwitch && this.driverWriteFlag.value && !(imitationSwitch || calculateSwitch)) {
                    return
                } else if (changeIndex === 1 && imitationSwitch) {
                    if (!(driverSwitch && this.driverWriteFlag.value)) this.driverSettingsSwitch.patchValue(false, {emitEvent: false})
                    this.calculateSettingsSwitch.patchValue(false, {emitEvent: false})
                } else if (changeIndex === 2 && calculateSwitch) {
                    if (!(driverSwitch && this.driverWriteFlag.value)) this.driverSettingsSwitch.patchValue(false, {emitEvent: false})
                    this.imitationSettingsSwitch.patchValue(false, {emitEvent: false})
                }
            })

        this.driverWriteFlag.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
                if (this.skipSwitching) return;
                if (!value && this.driverSettingsSwitch.value) {
                    this.imitationSettingsSwitch.patchValue(false, {emitEvent: false})
                    this.calculateSettingsSwitch.patchValue(false, {emitEvent: false})
                }
            })

        this.driverSettingsFormDriver.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((v) => {
                if (v) {
                    this.driverWriteFlag.enable()
                    this.driverSettingsFormObject.enable()
                } else {
                    this.driverWriteFlag.disable()
                    this.driverSettingsFormObject.disable()
                }
                this.driverSettingsFormObject.reset()
            })

        this.driverSettingsFormObject.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((v) => {
                if (v === 'LACERTA_DRIVER_PROTOCOL_CONN_STATUS' || v === 'LACERTA_DRIVER_TIMEOUT') {
                    this.driverWriteFlag.patchValue(this.readFlagsSelectOptions[0].value, {emitEvent: false})
                    this.driverWriteFlag.disable()
                } else {
                    if (this.driverWriteFlag.disabled && this.driverSettingsForm.enabled) {
                        let isDirty = this.objectForm.dirty
                        this.driverWriteFlag.enable()
                        if (!isDirty) {
                            this.objectForm.markAsPristine()
                        }
                    }
                }
            })

        this.eventSettingsSwitch.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(enabled => {
                if (enabled) {
                    this.eventSettings.get('event_settings_set_id').enable()
                } else {
                    this.eventSettings.get('event_settings_set_id').disable()
                }
                if (this.activeObject?.event_settings?.personal == true) {
                    this.eventSettings.get('event_settings_set_id').reset();
                }
            })

        this.driverSettingsSwitch.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(enabled => {
                if (enabled) {
                    this.driverSettingsForm.enable()
                } else {
                    this.driverSettingsForm.reset()
                    this.driverSettingsForm.disable()
                }
            })

        this.calculateSettingsSwitch.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(enabled => {
                if (enabled) {
                    this.calculateFormulaControl.enable();
                } else {
                    this.calculateFormulaControl.disable();
                }
            })

        combineLatest([this.imitationSettingsSwitch.valueChanges, this.imitationSettingsType.valueChanges])
            .pipe(
                untilDestroyed(this),
                distinctUntilChanged((x, y) => isEqual(x, y))
            )
            .subscribe(([status, type]) => {
                if (status) {
                    this.imitationSettingsType.status === 'DISABLED' && this.imitationSettingsForm.enable()
                    switch (getImitationComplexity(type)) {
                        case ImitationComplexity.Low:
                            this.imitationSettingsForm.get('discrete_ms').enable()
                            this.imitationSettingsForm.get('shift').disable()
                            this.imitationSettingsForm.get('period_s').disable()
                            this.imitationSettingsForm.get('amplitude').disable()
                            this.imitationSettingsForm.get('start_phase').disable()
                            break
                        case ImitationComplexity.Medium:
                            this.imitationSettingsForm.get('discrete_ms').enable()
                            this.imitationSettingsForm.get('shift').enable()
                            this.imitationSettingsForm.get('period_s').disable()
                            this.imitationSettingsForm.get('amplitude').disable()
                            this.imitationSettingsForm.get('start_phase').disable()
                            break
                        case ImitationComplexity.High:
                            this.imitationSettingsForm.get('discrete_ms').enable()
                            this.imitationSettingsForm.get('shift').enable()
                            this.imitationSettingsForm.get('period_s').enable()
                            this.imitationSettingsForm.get('period_s').addValidators(moreOrEqualOne)
                            this.imitationSettingsForm.get('period_s').updateValueAndValidity()
                            this.imitationSettingsForm.get('amplitude').enable()
                            this.imitationSettingsForm.get('start_phase').enable()
                            break
                        case ImitationComplexity.Random:
                            this.imitationSettingsForm.get('discrete_ms').enable()
                            this.imitationSettingsForm.get('shift').enable()
                            this.imitationSettingsForm.get('period_s').enable()
                            this.imitationSettingsForm.get('period_s').clearValidators()
                            this.imitationSettingsForm.get('period_s').updateValueAndValidity()
                            this.imitationSettingsForm.get('amplitude').disable()
                            this.imitationSettingsForm.get('start_phase').disable()
                            break
                    }

                    this.imitationSettingsForm.markAsUntouched()
                } else {
                    this.imitationSettingsType.status !== 'DISABLED' && this.imitationSettingsForm.disable()
                }
            })
    }
}
